import React, { useEffect, useState, useContext, useRef } from "react";
import Page from "components/ui/Page";
import Header from "components/ui/Header";
import { Box, Flex, Grid, Link, Spinner, Button } from "theme-ui";
import { BackArrowIcon, CheckMarkIcon, PlusIcon, ProductsIcon, TrashIcon, PencilIcon } from "components/svg";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import { Tab, Tabs } from "components/ui/Tabs";
import { themevals } from "theme/themevals";
import BasicButton from "components/forms/BasicButton";
import H5 from "components/typography/H5";
import Subtext from "components/typography/Subtext";
import TextInput from "components/forms/TextInput";
import { getProject } from "api/Projects";
import Separator from "components/ui/Separator";
import SelectInput from "components/forms/SelectInput";
import MultiText from "components/forms/MultiText";
import { Modal, ModalBody, ModalFooter, ModalHeader } from "components/ui/Modal";
import { ModalIcon, ModalSubtitle, ModalTitle, ModalTitleWrapper } from "components/ui/Header/styled";
import P from "components/typography/P";
import SearchField from "components/forms/SearchField";
import Checkbox from "components/forms/Checkbox";
import { getProducts } from "api/Products";
import { getServices } from "api/Services";
import { getDepot } from "api/Providers/Depots";
import { getProduct } from "api/Products";
import { getService } from "api/Services";
import { createColumnHelper } from "@tanstack/react-table";
import TableBasic from "components/ui/Table";
import Span from "components/typography/Span";
import UserContext from "context/User";
import { createProjectQuote, resolveProjectLine, getProjectCoverage } from "api/Projects";
import { useToast } from "components/toast";
import { trackActivity } from "api/ActivityTracker";

const ProjectQuoteCreate = () => {
  const params = useParams();
  const { id } = params;
  const columnHelper = createColumnHelper();
  const { user } = useContext<any>(UserContext);
  const navigate = useNavigate();
  const toast = useToast();

  // N3 follow-on (Jack, Session 61): when this flow is opened from a work
  // order's "Change service type" button, it carries ?returnToWo=<woId>.
  // On cancel AND after a quote is generated we return to that work order
  // (the case-file workbench) rather than bouncing to the quote record -
  // the WO re-derives and the new quote is acceptable from there. Absent
  // the param (opened from the project) we keep the original behavior:
  // back to the project on cancel, to the new quote on generate.
  const [searchParams] = useSearchParams();
  const returnToWo = searchParams.get("returnToWo");
  const cancelHref = returnToWo
    ? `/maintenance/work-orders/${returnToWo}`
    : `/projects/${id}`;

  const [project, setProject] = useState<any>(null);
  const [items, setItems] = useState<any[]>([]);
  const [itemModalOpen, setItemModalOpen] = useState(false);
  const [addOnOpen, setAddOnOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [bulkProductOptions, setBulkProductOptions] = useState<any[]>([]);
  const [relatedServiceOptions, setRelatedServiceOptions] = useState<any[]>([]);
  // Serviceable coverage for this project's site: which (product, service)
  // pairs actually resolve to a covered depot. Used to flag/disable catalog
  // options that cannot be fulfilled here so the user does not pick an
  // impossible combination and hit "no depot covers this location".
  const [coveragePairs, setCoveragePairs] = useState<Array<{ productId: string; serviceId: string }>>([]);
  const [coverageProductIds, setCoverageProductIds] = useState<Set<string>>(new Set());
  const [coverageServiceIds, setCoverageServiceIds] = useState<Set<string>>(new Set());
  const [selectedDepot, setSelectedDepot] = useState<any>(null);
  const [selectedAddons, setSelectedAddons] = useState<any[]>([]);
  const [newAddons, setNewAddons] = useState<any[]>([]);
  const [submittingRowId, setSubmittingRowId] = useState<string | null>(null);
  const [editItemModalOpen, setEditItemModalOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState<any>(null);
  const [selectedBulkProduct, setSelectedBulkProduct] = useState<any>(null);
  const [selectedService, setSelectedService] = useState<any>(null);
  const [selectedProvider, setSelectedProvider] = useState<any>(null);
  const [canOpenEditModal, setCanOpenEditModal] = useState(false);
  const hasTrackedView = useRef(false);

  // Add-item modal depot auto-resolution state (add modal ONLY; the edit
  // modal keeps its manual provider/depot selects). resolvedPick is the
  // auto-picked default shown in the modal; resolvedAlternatives is the
  // ranked serviceable-depot list behind the "Change" affordance;
  // resolveState tracks the request + provenance for display.
  const [resolvedPick, setResolvedPick] = useState<any>(null);
  const [resolvedAlternatives, setResolvedAlternatives] = useState<any[]>([]);
  const [resolveState, setResolveState] = useState<
    "idle" | "loading" | "estimate" | "computed" | "unavailable"
  >("idle");
  const [changeDepotOpen, setChangeDepotOpen] = useState(false);

  useEffect(() => {
    document.title = process.env.REACT_APP_NAME + " | Project Quote";
    _init();
  }, []);

  useEffect(() => {
    if (!project?.id) return;
    if (hasTrackedView.current) return;

    hasTrackedView.current = true;

    void trackActivity({
      module: "quotes",
      action: "view_project_quote_create",
      actionType: "view",
      userId: user?.id || null,
      companyId: user?.company?.id || null,
      entityType: "project",
      entityId: project.id,
      userAgent: typeof navigator !== "undefined" ? navigator.userAgent : null,
    });
  }, [project, user]);

  const _init = async () => {
    const res = await getProject({ data: { id } });
    if (res.id) {
      setProject(res);
    }
    const bulkProducts = await getProducts({ query: { filters: { addon: "1:isnot" } } });
    setBulkProductOptions(bulkProducts.items.map((p: any) => ({ value: p.id, label: p.name })));

    const services = await getServices({});
    setRelatedServiceOptions(services.items.map((s: any) => ({ value: s.id, label: s.name })));

    // Load serviceable coverage for this site so the product/service pickers
    // can flag options that cannot be fulfilled here.
    const coverage = await getProjectCoverage({ id });
    setCoveragePairs(Array.isArray(coverage?.pairs) ? coverage.pairs : []);
    setCoverageProductIds(new Set(Array.isArray(coverage?.productIds) ? coverage.productIds : []));
    setCoverageServiceIds(new Set(Array.isArray(coverage?.serviceIds) ? coverage.serviceIds : []));
  };

  // ADD-ITEM MODAL depot auto-resolution. Once product + service are chosen,
  // ask the backend to resolve the depot the SAME way an Instant Estimate
  // does: from the project's site address, no operator depot pick. Fills
  // provider/depot/cost/markup/quantity from the returned default and stores
  // the ranked alternatives behind the "Change" affordance. The depot record
  // is fetched so the add-on picker (selectedDepot.products) still works.
  // Every field stays editable. Keyed on [bulkProduct, relatedService];
  // depot is now an OUTPUT, not an input.
  const resolveLine = async (values: any, setFieldValue: (name: string, val?: any) => void) => {
    if (!values?.bulkProduct || !values?.relatedService) return;

    setResolveState("loading");
    setChangeDepotOpen(false);

    const res = await resolveProjectLine({
      id,
      data: {
        productId: values.bulkProduct,
        serviceId: values.relatedService,
      },
    });

    if (!res || res.source === "unavailable" || !res.default) {
      // Nothing serves this site: clear the resolved pick, leave fields blank
      // for manual entry (prior behavior), keep whatever alternatives came
      // back (none, in practice).
      setResolvedPick(null);
      setResolvedAlternatives(res?.alternatives ?? []);
      setResolveState("unavailable");
      setSelectedDepot(null);
      setFieldValue("provider", undefined);
      setFieldValue("depot", undefined);
      return;
    }

    const pick = res.default;
    setResolvedPick(pick);
    setResolvedAlternatives(res.alternatives ?? []);
    setResolveState(res.source === "estimate" ? "estimate" : "computed");

    // Wire the resolved depot into form state (provider + depot feed the
    // persist path and the depot fetch below).
    setFieldValue("provider", pick.providerId);
    setFieldValue("depot", pick.depotId);
    setFieldValue("unitCost", pick.cost);
    setFieldValue("markup", pick.markup);
    const quantityEmpty =
      values.quantity === undefined || values.quantity === null || values.quantity === "";
    if (pick.quantity != null && quantityEmpty) {
      setFieldValue("quantity", pick.quantity);
    }

    // Fetch the full depot record so the add-on picker has its products.
    if (pick.providerId && pick.depotId) {
      const depotRes = await getDepot({ data: { id: pick.depotId }, provider: { id: pick.providerId } });
      if (depotRes?.id) setSelectedDepot(depotRes);
    }
  };

  // Override: operator picked a different serviceable depot from the "Change"
  // list. Each alternative was already priced live at resolve time (same
  // formula as the default), so we fill straight from the alt row rather than
  // re-hitting the backend. Swaps the resolved pick + the depot record
  // driving add-ons. Fields stay editable.
  const selectAlternativeDepot = async (alt: any, setFieldValue: (name: string, val?: any) => void) => {
    setResolvedPick(alt);
    setChangeDepotOpen(false);
    setFieldValue("provider", alt.providerId);
    setFieldValue("depot", alt.depotId);
    setFieldValue("unitCost", alt.cost);
    setFieldValue("markup", alt.markup);

    if (alt.providerId && alt.depotId) {
      const depotRes = await getDepot({ data: { id: alt.depotId }, provider: { id: alt.providerId } });
      if (depotRes?.id) setSelectedDepot(depotRes);
    }
  };

  // Reset resolution state when the add-item modal closes so the next open
  // starts clean.
  const resetResolution = () => {
    setResolvedPick(null);
    setResolvedAlternatives([]);
    setResolveState("idle");
    setChangeDepotOpen(false);
  };

  // EDIT-MODAL seed. The edit modal opens on an existing line that already has a
  // depot, so we do not start "idle" - we resolve immediately for the line's
  // current product+service to (a) show the resolved-pick display seeded to the
  // line's current depot and (b) populate the alternatives for "Change". This
  // reuses resolveLine, then re-selects the line's existing depot as the pick so
  // the operator sees what is already on the line, not a re-picked default.
  // Called once when the edit modal's line is set. Product is LOCKED on edit;
  // changing the SERVICE re-runs resolveLine (new default + alternatives), which
  // is the Fibar install-only <-> all-inclusive motion.
  const seedEditResolution = async (
    productId: string,
    serviceId: string,
    currentDepotId: string,
    currentProviderId: string,
    setFieldValue: (name: string, val?: any) => void,
  ) => {
    if (!productId || !serviceId) return;
    setResolveState("loading");
    setChangeDepotOpen(false);

    const res = await resolveProjectLine({ id, data: { productId, serviceId } });

    if (!res || res.source === "unavailable" || !res.default) {
      // No depot serves the site for this product+service. Keep the line's own
      // stored depot as an informational pick so the operator is not stranded;
      // fields remain editable.
      setResolvedAlternatives(res?.alternatives ?? []);
      setResolveState("unavailable");
      return;
    }

    const alts = res.alternatives ?? [];
    setResolvedAlternatives(alts);
    setResolveState(res.source === "estimate" ? "estimate" : "computed");

    // Prefer the line's CURRENT depot as the shown pick (the operator opened an
    // existing line - show what is on it). Fall back to the resolved default if
    // the current depot no longer serves the site.
    const current = alts.find((a: any) => a.depotId === currentDepotId);
    const pick = current ?? res.default;
    setResolvedPick(pick);
    setFieldValue("provider", pick.providerId ?? currentProviderId);
    setFieldValue("depot", pick.depotId ?? currentDepotId);
  };

  const submitUpdate = async (values: any) => {
    const transformedItems = items.map((group) =>
      group.map((item: any) => {
        if (item.type === "service" && item.quantity === 0) {
          return { ...item, quantity: 1 };
        }
        return item;
      })
    );

    const quoteData = {
      project: { id: id },
      contactsCC: values.contactsCC || [],
      primaryClient: { id: values.primaryClient },
      projectName: values.projectName,
      client: { id: project.client.id },
      company: { id: user.company.id },
      lineitems: transformedItems.map((group) => ({ lineItems: group })),
      fullProject: project,
    };

    const response = await createProjectQuote({ id, data: quoteData });

    if (response?.id) {
      void trackActivity({
        module: "quotes",
        action: "create_quote",
        actionType: "create",
        userId: user?.id || null,
        companyId: user?.company?.id || null,
        entityType: "quote",
        entityId: response.id,
        metadata: {
          source: "project",
          projectId: project?.id || id || null,
        },
        userAgent: typeof navigator !== "undefined" ? navigator.userAgent : null,
      });

      toast.add(`Quote created.`, "var(--theme-ui-colors-success)");
      // Return to the originating work order when opened from one, else
      // to the new quote (the project-opened default).
      navigate(returnToWo ? `/maintenance/work-orders/${returnToWo}` : `/quotes/${response.id}`);
    } else {
      toast.add(`Error creating quote.`, "var(--theme-ui-colors-danger)");
      console.error("Quote creation failed:", response);
    }
  };

  const _validate = Yup.object().shape({
    projectName: Yup.string().required("Project name is required."),
  });

  const toggleItemModal = () => {
    if (itemModalOpen) {
      resetResolution();
      setSelectedDepot(null);
    }
    setItemModalOpen(!itemModalOpen);
  };

  const submitItemAdd = async (values: any) => {
    const newItem = await transformEditedItem(values);
    const newEntityIds = newItem[0]
      .map((item: any) => item.entityId)
      .sort()
      .join(",");

    const exists = items.some((group) => {
      const groupEntityIds = group
        .map((item: any) => item.entityId)
        .sort()
        .join(",");
      return groupEntityIds === newEntityIds;
    });

    setItems([...items, newItem[0]]);
    setItemModalOpen(false);
  };

  const transformEditedItem = async (values: any) => {
    const result: any[] = [];

    const depotRes = await getDepot({ data: { id: values.depot }, provider: { id: values.provider } });

    // Bulk product
    if (values.bulkProduct) {
      const bulkProductRes = await getProduct({ data: { id: values.bulkProduct } });
      result.push({
        id: values.bulkProduct,
        name: bulkProductRes.name || "", // optional
        description: bulkProductRes.description,
        cost: values.unitCost || 0,
        markup: values.markup || 0,
        price: Math.round((values.unitCost || 0) * (1 + (values.markup || 0) / 100) * 100) / 100, // rounded to 2 decimals
        unit: bulkProductRes.unit || "",
        entityId: values.bulkProduct,
        position: 0,
        type: "product",
        itemGroup: 0,
        depot: depotRes,
        quantity: values.quantity,
      });
    }

    // Addons
    if (Array.isArray(values.addons) && values.addons.length > 0) {
      values.addons.forEach((addon: any) => {
        result.push({
          id: addon.id || undefined,
          name: addon.name,
          description: addon.description || null,
          quantity: addon.quantity || 0,
          cost: addon.cost || 0,
          markup: addon.markup || 0,
          price: Math.round((addon.cost || 0) * (1 + (addon.markup || 0) / 100) * 100) / 100, // rounded to 2 decimals
          unit: addon.unit || "",
          entityId: addon.entityId,
          position: addon.position || 0,
          type: "addon-product",
          itemGroup: addon.itemGroup || 0,
          depot: depotRes,
        });
      });
    }

    // Related service
    if (values.relatedService) {
      const serviceRes = await getService({ data: { id: values.relatedService } });
      result.push({
        id: values.relatedService,
        name: serviceRes.name,
        description: serviceRes.description,
        quantity: 0,
        cost: 0,
        markup: 0,
        price: 0,
        unit: "yds", // or whatever the service uses
        entityId: values.relatedService,
        position: 0,
        type: "service",
        itemGroup: 0,
        depot: depotRes,
      });
    }

    return [result]; // nested array
  };

  const submitItemEdit = async (values: any) => {
    const productItem = selectedRow.find((i: any) => i.type === "product");
    const newItem = await transformEditedItem(values);

    setItems((prevItems) =>
      prevItems.map((group) => {
        const groupEntityIds = group
          .map((item: any) => item.entityId)
          .sort()
          .join(",");
        const selectedEntityIds = selectedRow
          .map((item: any) => item.entityId)
          .sort()
          .join(",");
        return groupEntityIds === selectedEntityIds ? newItem[0] : group;
      })
    );

    toggleEditItemModal();
  };

  const toggleAddOnModal = () => {
    setAddOnOpen(!addOnOpen);
  };

  const calculateTotalPrice = (item: any) => {
    const productItem = item.find((i: any) => i.type === "product");
    const addonItems = item.filter((i: any) => i.type === "addon-product");
    const mainServiceTotal = productItem.quantity * parseFloat(productItem.price);
    const addonsTotal = addonItems.reduce((sum: number, addon: any) => sum + (addon.quantity > 0 ? addon.quantity * parseFloat(addon.price) : 0), 0);
    return formatCurrency(mainServiceTotal + addonsTotal);
  };

  const calculateTotalCost = (item: any) => {
    const productItem = item.find((i: any) => i.type === "product");
    const addonItems = item.filter((i: any) => i.type === "addon-product");
    const mainServiceTotal = productItem.quantity * parseFloat(productItem.price);
    const addonsTotal = addonItems.reduce((sum: number, addon: any) => sum + (addon.quantity > 0 ? addon.quantity * parseFloat(addon.price) : 0), 0);
    const totalPrice = mainServiceTotal + addonsTotal;
    const markupAmount = (totalPrice * parseFloat(productItem.markup)) / 100;
    return formatCurrency(totalPrice - markupAmount);
  };

  const formatCurrency = (amount: number) => {
    return amount.toLocaleString("en-US", { style: "currency", currency: "USD" });
  };

  useEffect(() => {
    if (canOpenEditModal && selectedRow && selectedDepot && selectedBulkProduct) {
      setEditItemModalOpen(true);
      setCanOpenEditModal(false); // Reset flag after opening
    }
  }, [canOpenEditModal, selectedRow, selectedDepot, selectedBulkProduct]);

  // On edit-open, fetch the line's current depot as a full object so the add-on
  // picker (selectedDepot.products) works. Depot RESOLUTION for the edit modal
  // is handled separately by seedEditResolution; this only seeds the object the
  // add-on list reads. (Previously also built provider/depot dropdown options,
  // now removed - the edit modal resolves the depot instead of picking it.)
  const getDepotDetails = async (depotId: string, providerId: string) => {
    const depotRes = await getDepot({ data: { id: depotId }, provider: { id: providerId } });
    if (depotRes?.id) {
      setSelectedDepot(depotRes);
    }
  };

  const toggleEditItemModal = (item?: any) => {
    if (item) {
      const productItem = item.find((i: any) => i.type === "product");
      const serviceItem = item.find((i: any) => i.type === "service");
      const addonItems = item.filter((i: any) => i.type === "addon-product");

      getDepotDetails(productItem.depot.id, productItem.depot.provider.id);

      setSelectedRow(item);
      setSelectedBulkProduct(productItem);
      setSelectedAddons(addonItems);
      setSelectedService(serviceItem.entityId);
      setSelectedProvider(productItem.depot.provider.id);
      setCanOpenEditModal(true);
    } else {
      setSelectedRow(null);
      setSelectedDepot(null);
      setSelectedProvider(null);
      setSelectedService(null);
      setEditItemModalOpen(false);
      setSelectedBulkProduct(null);
      setCanOpenEditModal(false);
      resetResolution();
    }
  };

  const columns = [
    columnHelper.accessor((row: any) => row?.name, {
      id: "id",
      cell: (info: any) => (
        <Span color="#000" fontWeight="500">
          {info.getValue()}
        </Span>
      ),
      header: () => <span>Name</span>,
      meta: {
        style: { width: "150px", minWidth: "150px" },
      },
    }),
    columnHelper.accessor((row: any) => row?.quantity, {
      id: "quantity",
      cell: (info: any) => {
        const quantity = info.getValue();
        const unit = info.row.original?.unit;
        return quantity !== undefined ? `${quantity} ${unit || ""}`.trim() : "";
      },
      header: () => <span>Quantity</span>,
    }),
    columnHelper.accessor((row: any) => row?.markup, {
      id: "markup",
      cell: (info: any) => `${info.getValue()}%`,
      header: () => <span>Markup</span>,
    }),
    columnHelper.accessor((row: any) => row?.cost, {
      id: "baseCost",
      cell: (info: any) => `$${info.getValue()}`,
      header: () => <span>Cost Per Unit</span>,
    }),

    columnHelper.accessor((row: any) => row?.price, {
      id: "price",
      cell: (info: any) => `$${info.getValue()}`,
      header: () => <span>Price Per Unit</span>,
    }),
    columnHelper.accessor((row: any) => row?.cost * row?.quantity, {
      id: "totalCost",
      cell: (info: any) => formatCurrency(info.getValue()),
      header: () => <span>Total Cost</span>,
    }),
    columnHelper.accessor((row: any) => row?.price * row?.quantity, {
      id: "totalPrice",
      cell: (info: any) => formatCurrency(info.getValue()),
      header: () => <span>Total Price</span>,
    }),
  ];

  // Decorate the product options with coverage: a product not serviceable at
  // this site (in no covered pair) is disabled with a clear label suffix, so
  // the user cannot pick a combination that will fail to resolve. Empty
  // coverage (e.g. no geocode) leaves everything enabled so the modal still
  // works and falls back to manual entry.
  const decoratedProductOptions = () => {
    if (!coverageProductIds.size) return bulkProductOptions;
    return bulkProductOptions.map((opt: any) => {
      const covered = coverageProductIds.has(`${opt.value}`);
      return covered
        ? opt
        : { ...opt, isDisabled: true, label: `${opt.label} (not available at this site)` };
    });
  };

  // Decorate the service options against the SELECTED product: a service is
  // enabled only if it pairs with the chosen product at a covered depot. With
  // no product chosen yet, fall back to "covered for any product at this site".
  const decoratedServiceOptions = (selectedProductId?: string | null) => {
    if (!coverageServiceIds.size) return relatedServiceOptions;
    const allowed = new Set<string>();
    if (selectedProductId) {
      for (const p of coveragePairs) {
        if (`${p.productId}` === `${selectedProductId}`) allowed.add(`${p.serviceId}`);
      }
    } else {
      for (const id of coverageServiceIds) allowed.add(`${id}`);
    }
    return relatedServiceOptions.map((opt: any) => {
      const covered = allowed.has(`${opt.value}`);
      return covered
        ? opt
        : { ...opt, isDisabled: true, label: `${opt.label} (not available here)` };
    });
  };

  return (
    <>
      <Header></Header>
      {!project && (
        <Box sx={{ textAlign: "center", paddingTop: "30px" }}>
          <Spinner size={25} strokeWidth={3} title="loading" />
        </Box>
      )}
      {project && (
        <Page
          title="Create Quote"
          subtitle="Add a quote to your existing project."
          backAction={
            <Link variant="back" href={cancelHref}>
              <BackArrowIcon />
              {returnToWo ? `Back to work order` : `Back to Project`}
            </Link>
          }
        >
          <Formik initialValues={{ projectName: project.projectName, primaryClient: project.primaryClient.id, contactsCC: project.contactsCC }} onSubmit={submitUpdate} validationSchema={_validate}>
            {({ isSubmitting, errors, values, submitCount, setFieldValue, handleSubmit }) => (
              <Form noValidate autoComplete="off">
                <Tabs
                  vertical
                  stickyFooter
                  footer={
                    <Flex sx={{ gap: "10px", flexGrow: 1, justifyContent: "flex-end" }}>
                      <Link href={cancelHref} sx={{ ...themevals.buttons.secondaryOutline, textDecoration: "none", margin: "0 10px 0 0" }}>
                        Cancel
                      </Link>
                      <BasicButton type="submit" $submitting={isSubmitting} styles={{ margin: "0 10px 0 0" }} $iconHeight="14px" $iconWidth="14px" $loadingColor="#fff" keepText={true}>
                        {!isSubmitting && <CheckMarkIcon fill={"#fff"} />}
                        Save Quote
                      </BasicButton>
                    </Flex>
                  }
                >
                  <Tab itemKey="general">
                    <Grid gap={6} columns={[1, "1fr 3fr"]} sx={{ paddingLeft: "30px", paddingRight: "30px" }}>
                      <Box>
                        <H5 fontFamily={"Poppins"} fontWeight={"600"} color={"#000"} marginTop={"0px"} marginBottom={"5px"}>
                          Basic Info
                        </H5>
                        <Subtext marginTop={0} marginBottom={0} fontSize={"12px"} fontWeight={"400"} fontFamily={"Poppins"}>
                          The project name is readonly and generated by the project you are attaching this quote to.
                        </Subtext>
                      </Box>
                      <Flex sx={{ flex: 1 }}>
                        <TextInput
                          name="projectName"
                          label="Project Name"
                          value={values.projectName}
                          onChange={(val: any) => setFieldValue("projectName", val)}
                          $errors={errors.projectName && submitCount > 0 ? errors.projectName : null}
                          $customStyles={{ flex: 1, marginBottom: "0px" }}
                          readonly={true}
                        />
                      </Flex>
                    </Grid>
                    <Separator />
                    <Grid gap={6} columns={[1, "1fr 3fr"]} sx={{ paddingLeft: "30px", paddingRight: "30px" }}>
                      <Box>
                        <H5 fontFamily={"Poppins"} fontWeight={"600"} color={"#000"} marginTop={"0px"} marginBottom={"5px"}>
                          Client Info
                        </H5>
                        <Subtext marginTop={0} marginBottom={0} fontSize={"12px"} fontWeight={"400"} fontFamily={"Poppins"}>
                          Contact information is readonly and is generated by the project you are attaching this quote to.
                        </Subtext>
                      </Box>
                      <Grid gap={"20px"} columns={[1, "1fr 1fr"]}>
                        <Flex sx={{ flex: 1 }}>
                          <SelectInput
                            name="primaryClient"
                            label={"Primary Client"}
                            value={values.primaryClient}
                            options={[{ label: project.primaryClient.name, value: project.primaryClient.id }]}
                            onChange={(val: any) => setFieldValue("primaryClient", val)}
                            $errors={errors.primaryClient && submitCount > 0 ? errors.primaryClient : null}
                            $customStyles={{ width: "100%" }}
                            disabled={true}
                          />
                        </Flex>
                        <Flex sx={{ flex: 1 }}>
                          <MultiText
                            name="contactsCC"
                            label="CC"
                            value={values.contactsCC}
                            onChange={(val: string[]) => setFieldValue("contactsCC", val)}
                            $errors={errors.contactsCC && submitCount > 0 ? errors.contactsCC : null}
                            $customStyles={{ width: "100%" }}
                            disabled={true}
                          />
                        </Flex>
                      </Grid>
                    </Grid>
                    <Separator />
                    <Grid gap={6} columns={[1, "1fr 3fr"]} sx={{ paddingLeft: "30px", paddingRight: "30px" }}>
                      <Box>
                        <H5 fontFamily={"Poppins"} fontWeight={"600"} color={"#000"} marginTop={"0px"} marginBottom={"5px"}>
                          Items
                        </H5>
                        <Subtext marginTop={0} marginBottom={0} fontSize={"12px"} fontWeight={"400"} fontFamily={"Poppins"}>
                          Add products and services needed on the quote.
                        </Subtext>
                        <Box sx={{ marginTop: "20px" }}>
                          <BasicButton scheme="secondaryOutline" type="button" onClick={() => toggleItemModal()}>
                            <PlusIcon /> Add item
                          </BasicButton>
                        </Box>
                      </Box>
                      <Flex>
                        {items.length > 0 ? (
                          <Flex sx={{ flexDirection: "column", gap: "10px", width: "100%" }}>
                            {items.map(function (item: any, index: number) {
                              const productItem = item.find((i: any) => i.type === "product");
                              const serviceItem = item.find((i: any) => i.type === "service");
                              const addonItems = item.filter((i: any) => i.type === "addon-product");
                              return (
                                <Flex
                                  key={index}
                                  sx={{
                                    flexDirection: "column",
                                    border: `1px solid ${themevals.colors.inputOutline}`,
                                    borderRadius: "6px",
                                    boxShadow: "0px 0px 12px 4px rgba(129, 129, 129, 0.07);",
                                    width: "100%",
                                  }}
                                >
                                  <Flex sx={{ flexDirection: "row", padding: "15px 20px 15px 20px", borderBottom: `1px solid ${themevals.colors.inputOutline}` }}>
                                    <Flex sx={{ flex: 1, alignItems: "center" }}>
                                      <Box sx={{ fontFamily: "Poppins", fontSize: "14px", fontWeight: "600", color: "#000", marginRight: "5px" }}>{productItem.name}</Box>
                                      <Box>-</Box>
                                      <Box sx={{ fontFamily: "Poppins", fontSize: "14px", fontWeight: "500", color: themevals.colors.dkgrey, marginLeft: "5px" }}>{serviceItem.name}</Box>
                                    </Flex>
                                    <Flex sx={{ flex: 1, justifyContent: "flex-end" }}>
                                      <BasicButton
                                        type="button"
                                        $submitting={submittingRowId === item.id}
                                        styles={{ margin: "0 0 0 10px" }}
                                        $iconHeight="14px"
                                        $iconWidth="14px"
                                        $loadingColor="#fff"
                                        keepText={true}
                                        onClick={() => toggleEditItemModal(item)}
                                        scheme={"secondaryOutline"}
                                      >
                                        {submittingRowId !== item.id && <PencilIcon fill={"#000"} />}
                                        Edit
                                      </BasicButton>
                                    </Flex>
                                  </Flex>
                                  <Grid gap={"30px"} columns={[1, "1fr 1fr 1fr 1fr"]} sx={{ padding: "20px" }}>
                                    <Flex sx={{ flexDirection: "column" }}>
                                      <Box sx={{ fontFamily: "Poppins", fontSize: "12px", fontWeight: "500", color: themevals.colors.dkgrey, marginBottom: "8px", lineHeight: "12px" }}>Quantity</Box>
                                      <Box sx={{ fontFamily: "Poppins", fontSize: "14px", fontWeight: "600", color: "#000" }}>
                                        {productItem.quantity} {productItem.unit}
                                      </Box>
                                    </Flex>
                                    <Flex sx={{ flexDirection: "column" }}>
                                      <Box sx={{ fontFamily: "Poppins", fontSize: "12px", fontWeight: "500", color: themevals.colors.dkgrey, marginBottom: "8px", lineHeight: "12px" }}>Markup</Box>
                                      <Box sx={{ fontFamily: "Poppins", fontSize: "14px", fontWeight: "600", color: "#000" }}>{productItem.markup}%</Box>
                                    </Flex>
                                    <Flex sx={{ flexDirection: "column" }}>
                                      <Box sx={{ fontFamily: "Poppins", fontSize: "12px", fontWeight: "500", color: themevals.colors.dkgrey, marginBottom: "8px", lineHeight: "12px" }}>
                                        Bulk Product Cost Per Unit
                                      </Box>
                                      <Box sx={{ fontFamily: "Poppins", fontSize: "14px", fontWeight: "600", color: "#000" }}>${productItem.cost}</Box>
                                    </Flex>
                                    <Flex sx={{ flexDirection: "column" }}>
                                      <Box sx={{ fontFamily: "Poppins", fontSize: "12px", fontWeight: "500", color: themevals.colors.dkgrey, marginBottom: "8px", lineHeight: "12px" }}>
                                        Bulk Product Price Per Unit
                                      </Box>
                                      <Box sx={{ fontFamily: "Poppins", fontSize: "14px", fontWeight: "600", color: "#000" }}>${productItem.price}</Box>
                                    </Flex>
                                    <Flex sx={{ flexDirection: "column" }}>
                                      <Box sx={{ fontFamily: "Poppins", fontSize: "12px", fontWeight: "500", color: themevals.colors.dkgrey, marginBottom: "8px", lineHeight: "12px" }}>
                                        Grand Total Cost
                                      </Box>
                                      <Box sx={{ fontFamily: "Poppins", fontSize: "14px", fontWeight: "600", color: "#000" }}>{calculateTotalCost(item)}</Box>
                                    </Flex>
                                    <Flex sx={{ flexDirection: "column" }}>
                                      <Box sx={{ fontFamily: "Poppins", fontSize: "12px", fontWeight: "500", color: themevals.colors.dkgrey, marginBottom: "8px", lineHeight: "12px" }}>
                                        Grand Total Price
                                      </Box>
                                      <Box sx={{ fontFamily: "Poppins", fontSize: "14px", fontWeight: "600", color: "#000" }}>{calculateTotalPrice(item)}</Box>
                                    </Flex>
                                    <Flex sx={{ flexDirection: "column" }}>
                                      <Box sx={{ fontFamily: "Poppins", fontSize: "12px", fontWeight: "500", color: themevals.colors.dkgrey, marginBottom: "8px", lineHeight: "12px" }}>Provider</Box>
                                      <Box sx={{ fontFamily: "Poppins", fontSize: "14px", fontWeight: "600", color: "#000" }}>{productItem.depot.provider.name}</Box>
                                    </Flex>
                                    <Flex sx={{ flexDirection: "column" }}>
                                      <Box sx={{ fontFamily: "Poppins", fontSize: "12px", fontWeight: "500", color: themevals.colors.dkgrey, marginBottom: "8px", lineHeight: "12px" }}>Depot</Box>
                                      <Box sx={{ fontFamily: "Poppins", fontSize: "14px", fontWeight: "600", color: "#000" }}>{productItem.depot.name}</Box>
                                    </Flex>
                                  </Grid>
                                  {addonItems.some((addon: any) => addon.quantity > 0) && (
                                    <Flex sx={{ flexDirection: "column", padding: "20px" }}>
                                      <H5 fontFamily="Poppins" fontSize="12px" marginLeft="0px" marginTop="0px" marginBottom="0px" color={themevals.colors.dkgrey}>
                                        Add-on Products
                                      </H5>
                                      <TableBasic
                                        data={addonItems}
                                        columns={columns}
                                        enablePaging={false}
                                        enableSearch={false}
                                        customStyles={{ border: `1px solid ${themevals.colors.inputOutline}` }}
                                      />
                                    </Flex>
                                  )}
                                </Flex>
                              );
                            })}
                          </Flex>
                        ) : (
                          <Flex>
                            <Subtext fontSize={"14px"} fontWeight={"400"} fontFamily={"Poppins"}>
                              No items have been added to the quote.
                            </Subtext>
                          </Flex>
                        )}
                      </Flex>
                    </Grid>
                  </Tab>
                </Tabs>
              </Form>
            )}
          </Formik>
          <Modal isOpen={itemModalOpen} toggle={() => toggleItemModal()} centered size={"md"}>
            <Formik
              initialValues={{
                addons: [],
              }}
              onSubmit={(values) => submitItemAdd(values)}
            >
              {({
                isSubmitting,
                values,
                setFieldValue,
                errors,
                submitCount,
              }: {
                isSubmitting: boolean;
                submitCount: number;
                values: any;
                setFieldValue: (name: string, val?: any) => void;
                errors: any;
              }) => {
                // Resolve the depot from the site address once product +
                // service are both chosen (depot is an OUTPUT now, not an
                // operator pick). Fills provider/depot/cost/markup/quantity
                // and populates the alternatives list behind "Change".
                useEffect(() => {
                  resolveLine(values, setFieldValue);
                }, [values.bulkProduct, values.relatedService]);

                useEffect(() => {
                  const mergedAddons = [...values.addons];

                  selectedAddons.forEach((newAddon) => {
                    const existingIndex = mergedAddons.findIndex((a) => a.entityId === newAddon.entityId);

                    if (existingIndex > -1) {
                      mergedAddons[existingIndex] = {
                        ...mergedAddons[existingIndex],
                        quantity: newAddon.quantity ?? mergedAddons[existingIndex].quantity,
                        markup: newAddon.markup ?? mergedAddons[existingIndex].markup,
                        cost: newAddon.cost ?? mergedAddons[existingIndex].cost,
                      };
                    } else {
                      mergedAddons.push({ ...newAddon, quantity: newAddon.quantity || 1 });
                    }
                  });

                  setFieldValue("addons", mergedAddons);
                }, [selectedAddons]);

                const submitRemoveAddonItem = (addonToRemove: any) => {
                  const updated = values.addons.filter((addon: any) => addon.entityId !== addonToRemove.entityId);
                  setFieldValue("addons", updated);
                  setSelectedAddons(updated);
                };

                const availableAddons =
                  selectedDepot?.products?.filter((product: any) => {
                    if (!product.addon) return false;
                    const existing = selectedAddons.find((addon: any) => addon.entityId === product.id);
                    return !existing || existing.quantity === 0;
                  }) ?? [];

                return (
                  <Form noValidate autoComplete="off">
                    <ModalHeader toggle={() => toggleItemModal()}>
                      <ModalIcon>
                        <ProductsIcon fill={"000"} />
                      </ModalIcon>
                      <ModalTitleWrapper>
                        <ModalTitle>Add Quote item</ModalTitle>
                        <ModalSubtitle>{"Add an item to the quote."}</ModalSubtitle>
                      </ModalTitleWrapper>
                    </ModalHeader>
                    <ModalBody>
                      <Flex sx={{ gap: "20px", flexDirection: "row" }}>
                        <Box sx={{ flex: 1 }}>
                          <SelectInput
                            name="bulkProduct"
                            label="Bulk Product"
                            value={values.bulkProduct}
                            options={decoratedProductOptions()}
                            $errors={errors.bulkProduct && submitCount > 0 ? errors.bulkProduct : null}
                            $customStyles={{ flexGrow: 1, width: "100%" }}
                            onChange={(val: any) => setFieldValue("bulkProduct", val)}
                          />
                        </Box>

                        <Box sx={{ flex: 1 }}>
                          <SelectInput
                            name="relatedService"
                            label="Related Service"
                            value={values.relatedService}
                            options={decoratedServiceOptions(values.bulkProduct)}
                            $errors={errors.relatedService && submitCount > 0 ? errors.relatedService : null}
                            $customStyles={{ flexGrow: 1, width: "100%" }}
                            onChange={(val: any) => setFieldValue("relatedService", val)}
                          />
                        </Box>
                      </Flex>

                      {/* Provider + depot are RESOLVED from the site address,
                          not picked. Show the auto-pick with a Change affordance
                          over the serviceable-depot alternatives. */}
                      <Box sx={{ marginTop: "30px" }}>
                        <Subtext fontFamily="Poppins" marginTop="0px" marginBottom="8px">
                          Provider and Depot
                        </Subtext>

                        {resolveState === "loading" && (
                          <Flex sx={{ alignItems: "center", gap: "10px", border: `1px solid ${themevals.colors.inputOutline}`, borderRadius: "6px", padding: "15px 20px" }}>
                            <Spinner size={16} strokeWidth={3} title="resolving" />
                            <P fontFamily="Poppins" fontSize="14px" marginTop="0px" marginBottom="0px" color={themevals.colors.dkgrey}>
                              Resolving the best depot for this site...
                            </P>
                          </Flex>
                        )}

                        {resolveState === "idle" && (
                          <Flex sx={{ border: `1px solid ${themevals.colors.inputOutline}`, borderRadius: "6px", padding: "15px 20px" }}>
                            <P fontFamily="Poppins" fontSize="14px" marginTop="0px" marginBottom="0px" color={themevals.colors.dkgrey}>
                              Choose a product and service and the depot will resolve automatically from the site address.
                            </P>
                          </Flex>
                        )}

                        {resolveState === "unavailable" && (
                          <Flex sx={{ border: `1px solid ${themevals.colors.inputOutline}`, borderRadius: "6px", padding: "15px 20px" }}>
                            <P fontFamily="Poppins" fontSize="14px" marginTop="0px" marginBottom="0px" color={themevals.colors.danger}>
                              No depot covers this location for the selected product and service. Enter cost and markup manually below.
                            </P>
                          </Flex>
                        )}

                        {(resolveState === "estimate" || resolveState === "computed") && resolvedPick && (
                          <Flex sx={{ flexDirection: "column", border: `1px solid ${themevals.colors.inputOutline}`, borderRadius: "6px" }}>
                            <Flex sx={{ padding: "15px 20px", justifyContent: "space-between", alignItems: "center", gap: "20px" }}>
                              <Flex sx={{ flexDirection: "column", gap: "3px" }}>
                                <Box sx={{ fontFamily: "Poppins", fontSize: "14px", fontWeight: "600", color: "#000" }}>
                                  {resolvedPick.providerName} - {resolvedPick.depotName}
                                </Box>
                                <Box sx={{ fontFamily: "Poppins", fontSize: "12px", fontWeight: "400", color: themevals.colors.dkgrey }}>
                                  {resolvedPick.serviceArea ? `${resolvedPick.serviceArea} - ` : ""}
                                  {formatCurrency(Number(resolvedPick.price) || 0)} per unit
                                  {resolveState === "estimate" ? " - from the original estimate" : " - at current rates"}
                                </Box>
                              </Flex>
                              {resolvedAlternatives.length > 1 && (
                                <BasicButton
                                  type="button"
                                  scheme="secondaryOutline"
                                  keepText={true}
                                  onClick={() => setChangeDepotOpen(!changeDepotOpen)}
                                >
                                  {changeDepotOpen ? "Close" : "Change"}
                                </BasicButton>
                              )}
                            </Flex>

                            {changeDepotOpen && resolvedAlternatives.length > 1 && (
                              <Flex sx={{ flexDirection: "column", borderTop: `1px solid ${themevals.colors.inputOutline}` }}>
                                {resolvedAlternatives.map((alt: any, altIdx: number) => {
                                  const isCurrent = alt.depotId === resolvedPick.depotId;
                                  return (
                                    <Flex
                                      key={alt.depotId}
                                      sx={{
                                        padding: "12px 20px",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                        gap: "20px",
                                        borderTop: altIdx === 0 ? "none" : `1px solid ${themevals.colors.inputOutline}`,
                                        backgroundColor: isCurrent ? themevals.colors.base_100 : "transparent",
                                        cursor: isCurrent ? "default" : "pointer",
                                      }}
                                      onClick={() => {
                                        if (!isCurrent) selectAlternativeDepot(alt, setFieldValue);
                                      }}
                                    >
                                      <Flex sx={{ flexDirection: "column", gap: "2px" }}>
                                        <Box sx={{ fontFamily: "Poppins", fontSize: "13px", fontWeight: "600", color: "#000" }}>
                                          {alt.providerName} - {alt.depotName}
                                          {alt.preferred ? " (preferred)" : ""}
                                        </Box>
                                        <Box sx={{ fontFamily: "Poppins", fontSize: "12px", fontWeight: "400", color: themevals.colors.dkgrey }}>
                                          {alt.serviceArea ? `${alt.serviceArea} - ` : ""}
                                          {formatCurrency(Number(alt.price) || 0)} per unit
                                        </Box>
                                      </Flex>
                                      {isCurrent && (
                                        <Box sx={{ fontFamily: "Poppins", fontSize: "12px", fontWeight: "600", color: themevals.colors.success }}>
                                          Selected
                                        </Box>
                                      )}
                                    </Flex>
                                  );
                                })}
                              </Flex>
                            )}
                          </Flex>
                        )}
                      </Box>

                      <Flex sx={{ gap: "20px", marginTop: "30px" }}>
                        <Box sx={{ flex: 1 }}>
                          <TextInput
                            name="unitCost"
                            label="Unit Cost"
                            type="number"
                            value={values.unitCost}
                            onChange={(val: any) => setFieldValue("unitCost", val)}
                            prefix={"$"}
                            $errors={errors?.unitCost?.base_cost && submitCount > 0 ? errors.unitCost : null}
                          />
                        </Box>
                        <Box sx={{ flex: 1 }}>
                          <TextInput
                            name="quantity"
                            label="Quantity"
                            type="number"
                            value={values.quantity}
                            onChange={(val: any) => setFieldValue("quantity", val)}
                            $errors={errors?.quantity && submitCount > 0 ? errors.quantity : null}
                          />
                        </Box>
                        <Box sx={{ flex: 1 }}>
                          <TextInput
                            name="markup"
                            label="Markup"
                            type="number"
                            value={values.markup}
                            onChange={(val: any) => setFieldValue("markup", val)}
                            $errors={errors?.markup && submitCount > 0 ? errors.markup : null}
                            suffix={"%"}
                          />
                        </Box>
                      </Flex>

                      <Flex>
                        <Subtext fontFamily="Poppins" marginTop="0px" marginBottom="8px">
                          Add-on Products
                        </Subtext>
                      </Flex>
                      <Flex sx={{ border: `1px solid ${themevals.colors.inputOutline}`, borderRadius: "6px", flexDirection: "column", marginBottom: "30px" }}>
                        <Flex sx={{ padding: "20px", gap: "20px", flexDirection: "column" }}>
                          {values.addons.filter((addon: any) => addon.quantity > 0).length > 0 &&
                            values.addons.map((addon: any, index: number) =>
                              addon.quantity > 0 ? (
                                <Flex
                                  key={index}
                                  sx={{
                                    gap: "10px",
                                    justifyContent: "flex-start",
                                    borderRadius: "5px",
                                    border: `1px solid ${themevals.colors.mdgrey}`,
                                    padding: "10px 15px",
                                    width: "100%",
                                  }}
                                >
                                  <Flex sx={{ flex: 1, alignItems: "center", justifyContent: "flex-start" }}>
                                    <P fontFamily="Poppins" marginTop="0px" marginBottom="0px" fontSize="14px" fontWeight="500">
                                      {addon.name}
                                    </P>
                                  </Flex>
                                  <Flex sx={{ alignItems: "center", justifyContent: "flex-end", flex: 1, gap: "20px" }}>
                                    <Flex sx={{ alignItems: "center", justifyContent: "flex-end" }}>
                                      <TextInput
                                        name={`addons[${index}].cost`}
                                        value={addon.cost}
                                        type="number"
                                        label="Base Cost"
                                        prefix={"$"}
                                        $customStyles={{ marginBottom: "0px" }}
                                        onChange={(val: any) => setFieldValue(`addons[${index}].cost`, val)}
                                        $errors={errors?.addons?.[index].cost && submitCount > 0 ? errors?.addons?.[index].cost : null}
                                      />
                                    </Flex>

                                    <Flex sx={{ alignItems: "center", justifyContent: "flex-end" }}>
                                      <TextInput
                                        name={`addons[${index}].quantity`}
                                        value={addon.quantity}
                                        type="number"
                                        label={"Quantity"}
                                        min={1}
                                        suffix={addon.unit}
                                        $customStyles={{ marginBottom: "0px" }}
                                        onChange={(val: any) => setFieldValue(`addons[${index}].quantity`, val)}
                                        $errors={errors?.addons?.[index].quantity && submitCount > 0 ? errors?.addons?.[index].quantity : null}
                                        onKeyDown={(e: any) => {
                                          if (e.key === "0" && (e.currentTarget.value === "" || e.currentTarget.value === "0")) {
                                            e.preventDefault();
                                          }
                                        }}
                                        onBlur={(e: any) => {
                                          const val = parseFloat(e.target.value);
                                          if (isNaN(val) || val < 1) {
                                            setFieldValue(`addons[${index}].quantity`, 1);
                                          }
                                        }}
                                      />
                                    </Flex>

                                    <Flex sx={{ alignItems: "center", justifyContent: "flex-end" }}>
                                      <TextInput
                                        name={`addons[${index}].markup`}
                                        value={addon.markup}
                                        type="number"
                                        label="Markup"
                                        suffix={"%"}
                                        $customStyles={{ marginBottom: "0px" }}
                                        onChange={(val: any) => setFieldValue(`addons[${index}].markup`, val)}
                                        $errors={errors?.addons?.[index].markup && submitCount > 0 ? errors?.addons?.[index].markup : null}
                                      />
                                    </Flex>
                                  </Flex>

                                  <Flex sx={{ alignItems: "flex-end", justifyContent: "flex-end", paddingLeft: "10px", alignSelf: "stretch", width: "30px" }}>
                                    <Flex sx={{ alignSelf: "stretch", alignItems: "flex-end", paddingBottom: "10px" }}>
                                      <Box sx={{ cursor: "pointer" }} onClick={() => submitRemoveAddonItem(addon)}>
                                        <TrashIcon fill={themevals.colors.dkgrey} width="14px" />
                                      </Box>
                                    </Flex>
                                  </Flex>
                                </Flex>
                              ) : null
                            )}
                          {values.addons.filter((addon: any) => addon.quantity > 0).length === 0 && (
                            <P fontFamily="Poppins" fontSize="14px" color={`${themevals.colors.dkgrey}`}>
                              There are no add-on products associated with this item.
                            </P>
                          )}
                        </Flex>
                        {availableAddons.length > 0 && (
                          <Flex sx={{ borderTop: `1px solid ${themevals.colors.inputOutline}`, padding: "20px", justifyContent: "flex-end" }}>
                            <BasicButton
                              type="button"
                              scheme="secondaryOutline"
                              $submitting={isSubmitting}
                              $iconHeight="14px"
                              $iconWidth="14px"
                              $loadingColor="#000"
                              keepText={true}
                              onClick={() => toggleAddOnModal()}
                            >
                              {!isSubmitting && <PlusIcon fill={"#000"} />}
                              Add product
                            </BasicButton>
                          </Flex>
                        )}
                      </Flex>
                    </ModalBody>
                    <ModalFooter>
                      <Button variant="secondaryOutline" type="button" onClick={() => toggleItemModal()} style={{ marginRight: "10px" }}>
                        Cancel
                      </Button>
                      <BasicButton type="submit" $submitting={isSubmitting} $iconHeight="14px" $iconWidth="14px" $loadingColor="#fff" keepText={true}>
                        {!isSubmitting && <CheckMarkIcon fill={"#fff"} />}
                        Save item
                      </BasicButton>
                    </ModalFooter>
                  </Form>
                );
              }}
            </Formik>
          </Modal>
          <Modal isOpen={addOnOpen} toggle={() => toggleAddOnModal()} centered size={"sm"}>
            <Formik
              initialValues={{ addons: selectedAddons }}
              onSubmit={() => {
                setAddOnOpen(false);
              }}
              enableReinitialize
            >
              {({
                isSubmitting,
                values,
                setFieldValue,
                submitCount,
                errors,
              }: {
                isSubmitting: boolean;
                values: any;
                setFieldValue: (name: string, val?: any) => void;
                submitCount: number;
                errors: any;
              }) => {
                const toggleSelectAll = () => {
                  if (newAddons.length === availableAddons.length) {
                    // If all are selected, deselect all
                    setNewAddons([]);
                  } else {
                    // Select all available add-ons
                    setNewAddons(
                      availableAddons.map((product: any) => ({
                        ...product,
                        addonId: product.id,
                        quantity: 1,
                        cost: product.cost,
                      }))
                    );
                  }
                };

                const handleAddSelectedAddons = () => {
                  setSelectedAddons((prevAddons) => [...prevAddons, ...newAddons]);
                  setNewAddons([]);
                  setAddOnOpen(false);
                };

                const handleAddonSelect = (product: any) => {
                  const isSelected = newAddons.some((addon) => addon.entityId === product.id);

                  if (isSelected) {
                    // Uncheck: remove the addon
                    setNewAddons((prev) => prev.filter((addon) => addon.entityId !== product.id));
                  } else {
                    // Check: add the addon
                    const existing = selectedAddons.find((addon: any) => addon.entityId === product.id);
                    setNewAddons((prev) => [
                      ...prev,
                      {
                        ...product,
                        entityId: product.id,
                        quantity: 1,
                        cost: existing?.cost ?? product.cost,
                        markup: existing?.markup ?? 0,
                      },
                    ]);
                  }
                };

                const availableAddons =
                  selectedDepot?.products?.filter((product: any) => {
                    if (product.addon !== true) return false;
                    const existing = selectedAddons.find((addon: any) => addon.entityId === product.id);
                    return !existing || existing.quantity === 0;
                  }) ?? [];

                return (
                  <Form noValidate autoComplete="off">
                    <ModalHeader toggle={() => toggleAddOnModal()}>
                      <ModalIcon>
                        <ProductsIcon fill={"000"} />
                      </ModalIcon>
                      <ModalTitleWrapper>
                        <ModalTitle>Add products</ModalTitle>
                        <ModalSubtitle>{"Add available products at this depot."}</ModalSubtitle>
                      </ModalTitleWrapper>
                    </ModalHeader>
                    <ModalBody>
                      <SearchField value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} width="100%" />
                      <Flex sx={{ marginTop: "20px", marginBottom: "10px", alignItems: "center", paddingLeft: "11px" }}>
                        <Checkbox label="Select All" onChange={toggleSelectAll} checked={false} />
                      </Flex>
                      {availableAddons.map((product: any, idx: number) => (
                        <Flex
                          key={product.id}
                          sx={{
                            gap: "1rem",
                            alignItems: "center",
                            marginBottom: "10px",
                            border: `1px solid ${values.addons.includes(product.id) ? "black" : themevals.colors.mdgrey}`,
                            borderRadius: "6px",
                            height: "38px",
                            padding: "0px 10px",
                          }}
                        >
                          <Checkbox label={product.name} checked={newAddons.some((addon) => addon.id === product.id)} onChange={() => handleAddonSelect(product)} labelSize="14px" />
                        </Flex>
                      ))}
                    </ModalBody>
                    <ModalFooter>
                      <Button variant="secondaryOutline" type="button" onClick={() => setAddOnOpen(false)} style={{ marginRight: "10px" }}>
                        Cancel
                      </Button>
                      <BasicButton type="button" onClick={() => handleAddSelectedAddons()} $submitting={isSubmitting} $iconHeight="14px" $iconWidth="14px" $loadingColor="#fff" keepText={true}>
                        {!isSubmitting && <PlusIcon fill={"#fff"} />}
                        Add products
                      </BasicButton>
                    </ModalFooter>
                  </Form>
                );
              }}
            </Formik>
          </Modal>
          <Modal isOpen={editItemModalOpen} toggle={() => toggleEditItemModal()} centered size={"md"}>
            <Formik
              initialValues={{
                addons: selectedAddons,
                relatedService: selectedService,
                bulkProduct: selectedBulkProduct?.entityId,
                provider: selectedProvider,
                depot: typeof selectedDepot === "string" ? selectedDepot : selectedDepot?.id,
                unitCost: selectedBulkProduct?.cost,
                quantity: selectedBulkProduct?.quantity,
                markup: selectedBulkProduct?.markup,
              }}
              onSubmit={(values) => submitItemEdit(values)}
            >
              {({
                isSubmitting,
                values,
                setFieldValue,
                errors,
                submitCount,
              }: {
                isSubmitting: boolean;
                submitCount: number;
                values: any;
                setFieldValue: (name: string, val?: any) => void;
                errors: any;
              }) => {
                const submitRemoveAddonItem = (addonToRemove: any) => {
                  const updated = values.addons.filter((addon: any) => addon.entityId !== addonToRemove.entityId);
                  setFieldValue("addons", updated);
                  setSelectedAddons(updated);
                };

                // Seed the resolved-pick display ONCE when the edit modal opens
                // on a line. Product is locked; the pick is seeded to the line's
                // current depot with the serviceable alternatives behind Change.
                useEffect(() => {
                  if (editItemModalOpen && selectedBulkProduct?.entityId && selectedService) {
                    seedEditResolution(
                      selectedBulkProduct.entityId,
                      selectedService,
                      typeof selectedDepot === "string" ? selectedDepot : selectedDepot?.id,
                      selectedProvider,
                      setFieldValue,
                    );
                  }
                }, [editItemModalOpen]);

                // Changing the SERVICE on an existing line re-resolves the depot
                // for the new service (Fibar install-only <-> all-inclusive). The
                // line is a draft in a not-yet-accepted quote, so this only stages
                // the new scope; down-chain supersession happens at acceptance
                // (Phase C). Product is locked, so no product-change branch.
                const didMountServiceRef = useRef(false);
                useEffect(() => {
                  if (!didMountServiceRef.current) {
                    didMountServiceRef.current = true;
                    return;
                  }
                  if (values.bulkProduct && values.relatedService) {
                    resolveLine(values, setFieldValue);
                  }
                }, [values.relatedService]);

                useEffect(() => {
                  const mergedAddons = [...values.addons];

                  selectedAddons.forEach((newAddon) => {
                    const existingIndex = mergedAddons.findIndex((a) => a.entityId === newAddon.entityId);

                    if (existingIndex > -1) {
                      mergedAddons[existingIndex] = {
                        ...mergedAddons[existingIndex],
                        quantity: newAddon.quantity ?? mergedAddons[existingIndex].quantity,
                        markup: newAddon.markup ?? mergedAddons[existingIndex].markup,
                        cost: newAddon.cost ?? mergedAddons[existingIndex].cost,
                      };
                    } else {
                      mergedAddons.push({ ...newAddon, quantity: newAddon.quantity || 1 });
                    }
                  });

                  setFieldValue("addons", mergedAddons);
                }, [selectedAddons]);

                const availableAddons =
                  selectedDepot?.products?.filter((product: any) => {
                    if (!product.addon) return false;
                    const existing = selectedAddons.find((addon: any) => addon.entityId === product.id);
                    return !existing || existing.quantity === 0;
                  }) ?? [];

                return (
                  <Form noValidate autoComplete="off">
                    <ModalHeader toggle={() => toggleEditItemModal()}>
                      <ModalIcon>
                        <ProductsIcon fill={"000"} />
                      </ModalIcon>
                      <ModalTitleWrapper>
                        <ModalTitle>Edit Quote item</ModalTitle>
                        <ModalSubtitle>{"Edit an item on the quote."}</ModalSubtitle>
                      </ModalTitleWrapper>
                    </ModalHeader>
                    <ModalBody>
                      <Flex sx={{ gap: "20px", flexDirection: "row" }}>
                        <Box sx={{ flex: 1 }}>
                          <SelectInput
                            name="bulkProduct"
                            label="Bulk Product"
                            value={values.bulkProduct}
                            options={bulkProductOptions}
                            $errors={errors.bulkProduct && submitCount > 0 ? errors.bulkProduct : null}
                            $customStyles={{ flexGrow: 1, width: "100%" }}
                            onChange={(val: any) => setFieldValue("bulkProduct", val)}
                            disabled={true}
                          />
                        </Box>

                        <Box sx={{ flex: 1 }}>
                          <SelectInput
                            name="relatedService"
                            label="Related Service"
                            value={values.relatedService}
                            options={decoratedServiceOptions(values.bulkProduct)}
                            $errors={errors.relatedService && submitCount > 0 ? errors.relatedService : null}
                            $customStyles={{ flexGrow: 1, width: "100%" }}
                            onChange={(val: any) => setFieldValue("relatedService", val)}
                          />
                        </Box>
                      </Flex>

                      {/* Edit modal: product is locked; service can change and
                          re-resolves the depot. Provider/depot is the same
                          resolved-pick + Change display as the add modal, seeded
                          to the line's current depot on open. */}
                      <Box sx={{ marginTop: "30px" }}>
                        <Subtext fontFamily="Poppins" marginTop="0px" marginBottom="8px">
                          Provider and Depot
                        </Subtext>

                        {resolveState === "loading" && (
                          <Flex sx={{ alignItems: "center", gap: "10px", border: `1px solid ${themevals.colors.inputOutline}`, borderRadius: "6px", padding: "15px 20px" }}>
                            <Spinner size={16} strokeWidth={3} title="resolving" />
                            <P fontFamily="Poppins" fontSize="14px" marginTop="0px" marginBottom="0px" color={themevals.colors.dkgrey}>
                              Resolving the best depot for this site...
                            </P>
                          </Flex>
                        )}

                        {resolveState === "unavailable" && (
                          <Flex sx={{ border: `1px solid ${themevals.colors.inputOutline}`, borderRadius: "6px", padding: "15px 20px" }}>
                            <P fontFamily="Poppins" fontSize="14px" marginTop="0px" marginBottom="0px" color={themevals.colors.danger}>
                              No depot covers this location for the selected service. Enter cost and markup manually below.
                            </P>
                          </Flex>
                        )}

                        {(resolveState === "estimate" || resolveState === "computed") && resolvedPick && (
                          <Flex sx={{ flexDirection: "column", border: `1px solid ${themevals.colors.inputOutline}`, borderRadius: "6px" }}>
                            <Flex sx={{ padding: "15px 20px", justifyContent: "space-between", alignItems: "center", gap: "20px" }}>
                              <Flex sx={{ flexDirection: "column", gap: "3px" }}>
                                <Box sx={{ fontFamily: "Poppins", fontSize: "14px", fontWeight: "600", color: "#000" }}>
                                  {resolvedPick.providerName} - {resolvedPick.depotName}
                                </Box>
                                <Box sx={{ fontFamily: "Poppins", fontSize: "12px", fontWeight: "400", color: themevals.colors.dkgrey }}>
                                  {resolvedPick.serviceArea ? `${resolvedPick.serviceArea} - ` : ""}
                                  {formatCurrency(Number(resolvedPick.price) || 0)} per unit
                                  {resolveState === "estimate" ? " - from the original estimate" : " - at current rates"}
                                </Box>
                              </Flex>
                              {resolvedAlternatives.length > 1 && (
                                <BasicButton
                                  type="button"
                                  scheme="secondaryOutline"
                                  keepText={true}
                                  onClick={() => setChangeDepotOpen(!changeDepotOpen)}
                                >
                                  {changeDepotOpen ? "Close" : "Change"}
                                </BasicButton>
                              )}
                            </Flex>

                            {changeDepotOpen && resolvedAlternatives.length > 1 && (
                              <Flex sx={{ flexDirection: "column", borderTop: `1px solid ${themevals.colors.inputOutline}` }}>
                                {resolvedAlternatives.map((alt: any, altIdx: number) => {
                                  const isCurrent = alt.depotId === resolvedPick.depotId;
                                  return (
                                    <Flex
                                      key={alt.depotId}
                                      sx={{
                                        padding: "12px 20px",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                        gap: "20px",
                                        borderTop: altIdx === 0 ? "none" : `1px solid ${themevals.colors.inputOutline}`,
                                        backgroundColor: isCurrent ? themevals.colors.base_100 : "transparent",
                                        cursor: isCurrent ? "default" : "pointer",
                                      }}
                                      onClick={() => {
                                        if (!isCurrent) selectAlternativeDepot(alt, setFieldValue);
                                      }}
                                    >
                                      <Flex sx={{ flexDirection: "column", gap: "2px" }}>
                                        <Box sx={{ fontFamily: "Poppins", fontSize: "13px", fontWeight: "600", color: "#000" }}>
                                          {alt.providerName} - {alt.depotName}
                                          {alt.preferred ? " (preferred)" : ""}
                                        </Box>
                                        <Box sx={{ fontFamily: "Poppins", fontSize: "12px", fontWeight: "400", color: themevals.colors.dkgrey }}>
                                          {alt.serviceArea ? `${alt.serviceArea} - ` : ""}
                                          {formatCurrency(Number(alt.price) || 0)} per unit
                                        </Box>
                                      </Flex>
                                      {isCurrent && (
                                        <Box sx={{ fontFamily: "Poppins", fontSize: "12px", fontWeight: "600", color: themevals.colors.success }}>
                                          Selected
                                        </Box>
                                      )}
                                    </Flex>
                                  );
                                })}
                              </Flex>
                            )}
                          </Flex>
                        )}
                      </Box>

                      <Flex sx={{ gap: "20px", marginTop: "30px" }}>
                        <Box sx={{ flex: 1 }}>
                          <TextInput
                            name="unitCost"
                            label="Unit Cost"
                            type="number"
                            value={values.unitCost}
                            onChange={(val: any) => setFieldValue("unitCost", val)}
                            prefix={"$"}
                            $errors={errors?.unitCost?.base_cost && submitCount > 0 ? errors.unitCost : null}
                          />
                        </Box>
                        <Box sx={{ flex: 1 }}>
                          <TextInput
                            name="quantity"
                            label="Quantity"
                            type="number"
                            value={values.quantity}
                            onChange={(val: any) => setFieldValue("quantity", val)}
                            $errors={errors?.quantity && submitCount > 0 ? errors.quantity : null}
                          />
                        </Box>
                        <Box sx={{ flex: 1 }}>
                          <TextInput
                            name="markup"
                            label="Markup"
                            type="number"
                            value={values.markup}
                            onChange={(val: any) => setFieldValue("markup", val)}
                            $errors={errors?.markup && submitCount > 0 ? errors.markup : null}
                            suffix={"%"}
                          />
                        </Box>
                      </Flex>

                      <Flex>
                        <Subtext fontFamily="Poppins" marginTop="0px" marginBottom="8px">
                          Add-on Products
                        </Subtext>
                      </Flex>
                      <Flex sx={{ border: `1px solid ${themevals.colors.inputOutline}`, borderRadius: "6px", flexDirection: "column", marginBottom: "30px" }}>
                        <Flex sx={{ padding: "20px", gap: "20px", flexDirection: "column" }}>
                          {values.addons.filter((addon: any) => addon.quantity > 0).length > 0 &&
                            values.addons.map((addon: any, index: number) =>
                              addon.quantity > 0 ? (
                                <Flex
                                  key={index}
                                  sx={{
                                    gap: "10px",
                                    justifyContent: "flex-start",
                                    borderRadius: "5px",
                                    border: `1px solid ${themevals.colors.mdgrey}`,
                                    padding: "10px 15px",
                                    width: "100%",
                                  }}
                                >
                                  <Flex sx={{ flex: 1, alignItems: "center", justifyContent: "flex-start" }}>
                                    <P fontFamily="Poppins" marginTop="0px" marginBottom="0px" fontSize="14px" fontWeight="500">
                                      {addon.name}
                                    </P>
                                  </Flex>
                                  <Flex sx={{ alignItems: "center", justifyContent: "flex-end", flex: 1, gap: "20px" }}>
                                    <Flex sx={{ alignItems: "center", justifyContent: "flex-end" }}>
                                      <TextInput
                                        name={`addons[${index}].cost`}
                                        value={addon.cost}
                                        type="number"
                                        label="Base Cost"
                                        prefix={"$"}
                                        $customStyles={{ marginBottom: "0px" }}
                                        onChange={(val: any) => setFieldValue(`addons[${index}].cost`, val)}
                                        $errors={errors?.addons?.[index].cost && submitCount > 0 ? errors?.addons?.[index].cost : null}
                                      />
                                    </Flex>

                                    <Flex sx={{ alignItems: "center", justifyContent: "flex-end" }}>
                                      <TextInput
                                        name={`addons[${index}].quantity`}
                                        value={addon.quantity}
                                        type="number"
                                        label={"Quantity"}
                                        min={1}
                                        suffix={addon.unit}
                                        $customStyles={{ marginBottom: "0px" }}
                                        onChange={(val: any) => setFieldValue(`addons[${index}].quantity`, val)}
                                        $errors={errors?.addons?.[index].quantity && submitCount > 0 ? errors?.addons?.[index].quantity : null}
                                        onKeyDown={(e: any) => {
                                          if (e.key === "0" && (e.currentTarget.value === "" || e.currentTarget.value === "0")) {
                                            e.preventDefault();
                                          }
                                        }}
                                        onBlur={(e: any) => {
                                          const val = parseFloat(e.target.value);
                                          if (isNaN(val) || val < 1) {
                                            setFieldValue(`addons[${index}].quantity`, 1);
                                          }
                                        }}
                                      />
                                    </Flex>

                                    <Flex sx={{ alignItems: "center", justifyContent: "flex-end" }}>
                                      <TextInput
                                        name={`addons[${index}].markup`}
                                        value={addon.markup}
                                        type="number"
                                        label="Markup"
                                        suffix={"%"}
                                        $customStyles={{ marginBottom: "0px" }}
                                        onChange={(val: any) => setFieldValue(`addons[${index}].markup`, val)}
                                        $errors={errors?.addons?.[index].markup && submitCount > 0 ? errors?.addons?.[index].markup : null}
                                      />
                                    </Flex>
                                  </Flex>

                                  <Flex sx={{ alignItems: "flex-end", justifyContent: "flex-end", paddingLeft: "10px", alignSelf: "stretch", width: "30px" }}>
                                    <Flex sx={{ alignSelf: "stretch", alignItems: "flex-end", paddingBottom: "10px" }}>
                                      <Box sx={{ cursor: "pointer" }} onClick={() => submitRemoveAddonItem(addon)}>
                                        <TrashIcon fill={themevals.colors.dkgrey} width="14px" />
                                      </Box>
                                    </Flex>
                                  </Flex>
                                </Flex>
                              ) : null
                            )}
                          {values.addons.filter((addon: any) => addon.quantity > 0).length === 0 && (
                            <P fontFamily="Poppins" fontSize="14px" color={`${themevals.colors.dkgrey}`}>
                              There are no add-on products associated with this item.
                            </P>
                          )}
                        </Flex>
                        {availableAddons.length > 0 && (
                          <Flex sx={{ borderTop: `1px solid ${themevals.colors.inputOutline}`, padding: "20px", justifyContent: "flex-end" }}>
                            <BasicButton
                              type="button"
                              scheme="secondaryOutline"
                              $submitting={isSubmitting}
                              $iconHeight="14px"
                              $iconWidth="14px"
                              $loadingColor="#000"
                              keepText={true}
                              onClick={() => toggleAddOnModal()}
                            >
                              {!isSubmitting && <PlusIcon fill={"#000"} />}
                              Add product
                            </BasicButton>
                          </Flex>
                        )}
                      </Flex>
                    </ModalBody>
                    <ModalFooter>
                      <Button variant="secondaryOutline" type="button" onClick={() => toggleEditItemModal()} style={{ marginRight: "10px" }}>
                        Cancel
                      </Button>
                      <BasicButton type="submit" $submitting={isSubmitting} $iconHeight="14px" $iconWidth="14px" $loadingColor="#fff" keepText={true}>
                        {!isSubmitting && <CheckMarkIcon fill={"#fff"} />}
                        Save item
                      </BasicButton>
                    </ModalFooter>
                  </Form>
                );
              }}
            </Formik>
          </Modal>
        </Page>
      )}
    </>
  );
};

export default ProjectQuoteCreate;
